import React, { useState } from "react";
import { Checkbox } from "../../../components/controls/Checkbox";
import { ClubBenefits } from "../../../components/common/ClubBenefits";
import { useDispatch, useSelector } from "../../../hooks";
import { useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import { normalizeValue } from "../../../utils/helpers";
import { svgIcon } from "../../../utils/svgProvider";
import { handleFieldValueChange } from "../slices/register.slice";
import { showGeneralModal } from "../../settings/slices/settings.slice";

interface CompanyConsentsI {
	hideSalesConditions?: boolean | undefined;
	isClubMember?: boolean | undefined;
	target?: string;
}

export const CompanyConsents = (props: CompanyConsentsI) => {
	const [isConsentsInfoOpen, toggleConsentsInfoOpen] = useState(false);
	const dispatch = useDispatch();
	const { formatMessage } = useIntl();
	const target = props?.target || "b2b";

	const acceptSalesConditions = useSelector(
		(state) => state.registration[target].form.acceptSalesConditions,
	);

	const acceptSmsNotifications = useSelector(
		(state) => state.registration[target].form.acceptSmsNotifications,
	);

	const acceptOffersInformations = useSelector(
		(state) => state.registration[target].form.acceptOffersInformations,
	);

	const acceptMarketingConsents = useSelector(
		(state) => state.registration[target].form.acceptMarketingConsents,
	);

	const accept3rdPartyMarketingConsents = useSelector(
		(state) => state.registration[target].form.accept3rdPartyMarketingConsents,
	);

	const acceptClubAgreementConsents = useSelector(
		(state) => state.registration[target].form.acceptClubAgreementConsents,
	);

	const isBusinessConsentsEnabled = useSelector(
		(state) => state.settings.features.businessConsents,
	);

	const enableClubAgreementConsent = useSelector(
		(state) => state.settings.features.enableClubAgreementConsent,
	);

	const {
		register,
		trigger,
		setValue,
		clearErrors,
		formState: { errors },
	} = useFormContext();

	return (
		<>
			{!props?.hideSalesConditions && (
				<Checkbox
					key="acceptSalesConditions"
					id="acceptSalesConditions"
					ref={
						register("acceptSalesConditions", {
							value: acceptSalesConditions,
						})?.ref
					}
					value={acceptSalesConditions}
					error={
						typeof errors.acceptSalesConditions?.message === "string"
							? errors.acceptSalesConditions.message
							: undefined
					}
					className="form-input"
					label={formatMessage({
						id: "profile.SalesConditionsAccept",
					})}
					linkText={formatMessage({
						id: "profile.SalesConditionsAcceptLinkText",
					})}
					onChange={async ({ target: { id, value } }) => {
						value
							? clearErrors("acceptSalesConditions")
							: await trigger("acceptSalesConditions");
						setValue("acceptSalesConditions", normalizeValue(value));

						dispatch(
							handleFieldValueChange({
								form: target,
								field: id,
								value: normalizeValue(value),
							}),
						);
					}}
					onLinkClick={() =>
						dispatch(
							showGeneralModal({
								formattedTitle: {
									id: "komid.register.termsandconditions.title",
								},
								formattedContent: {
									id: "businessCondition",
									tagName: "div",
								},
							}),
						)
					}
				/>
			)}

			{isBusinessConsentsEnabled ? (
				<>
					<ClubBenefits target="form" />

					<div className="form-frame">
						{!props.isClubMember ? (
							<>
								<h2>
									{formatMessage({
										id: "komid.register.business.consents.title",
									})}
								</h2>
								<div className="form-text">
									<p>
										{formatMessage({
											id: "komid.register.business.consents.description",
										})}
									</p>
								</div>
							</>
						) : (
							<h2>
								{formatMessage({
									id: "komid.register.business.consents.club.title",
								})}
							</h2>
						)}

						<div className="form-text">
							<div className="form-more-content">
								<div
									className="form-more-content-trigger"
									onClick={() => toggleConsentsInfoOpen(!isConsentsInfoOpen)}
								>
									<strong>
										{formatMessage({
											id: "komid.register.business.consents.readmore",
										})}
									</strong>
									{isConsentsInfoOpen ? svgIcon.chevronUp : svgIcon.chevronDown}
								</div>
								{isConsentsInfoOpen && (
									<div
										className="form-more-content-content"
										dangerouslySetInnerHTML={{
											__html: formatMessage({
												id: "komid.register.business.consents.readmore.content",
											}),
										}}
									></div>
								)}
							</div>
						</div>

						<div className="form-text">
							<strong className="inner-space-8">
								{formatMessage({
									id: "komid.register.business.consents.checkboxes.label",
								})}
							</strong>

							{enableClubAgreementConsent ? (
								<Checkbox
									id="acceptClubAgreementConsents"
									ref={
										register("acceptClubAgreementConsents", {
											value: acceptClubAgreementConsents,
										})?.ref
									}
									value={acceptClubAgreementConsents}
									className="form-input"
									label={formatMessage({
										id: "consents.clubAgreement.text",
									})}
									linkText={formatMessage({
										id: "consents.clubAgreementLinks.text",
									})}
									onChange={async ({ target: { id, value } }) => {
										value
											? clearErrors("acceptClubAgreementConsents")
											: await trigger("acceptClubAgreementConsents");
										setValue(
											"acceptClubAgreementConsents",
											normalizeValue(value),
										);

										dispatch(
											handleFieldValueChange({
												form: target,
												field: id,
												value: normalizeValue(value),
											}),
										);
									}}
								/>
							) : (
								<>
									<Checkbox
										id="acceptMarketingConsents"
										ref={
											register("acceptMarketingConsents", {
												value: acceptMarketingConsents,
											})?.ref
										}
										value={acceptMarketingConsents}
										className="form-input"
										label={formatMessage({
											id: "consents.clubEmail.text",
										})}
										onChange={async ({ target: { id, value } }) => {
											value
												? clearErrors("acceptMarketingConsents")
												: await trigger("acceptMarketingConsents");

											setValue(
												"acceptMarketingConsents",
												normalizeValue(value),
											);

											dispatch(
												handleFieldValueChange({
													form: target,
													field: id,
													value: normalizeValue(value),
												}),
											);
										}}
									/>

									<Checkbox
										id="accept3rdPartyMarketingConsents"
										ref={
											register("accept3rdPartyMarketingConsents", {
												value: accept3rdPartyMarketingConsents,
											})?.ref
										}
										value={accept3rdPartyMarketingConsents}
										className="form-input"
										label={formatMessage({
											id: "consents.marketingCookie.text",
										})}
										onChange={async ({ target: { id, value } }) => {
											value
												? clearErrors("accept3rdPartyMarketingConsents")
												: await trigger("accept3rdPartyMarketingConsents");
											setValue(
												"accept3rdPartyMarketingConsents",
												normalizeValue(value),
											);

											dispatch(
												handleFieldValueChange({
													form: target,
													field: id,
													value: normalizeValue(value),
												}),
											);
										}}
									/>

									{(errors?.acceptMarketingConsents?.message ||
										errors?.accept3rdPartyMarketingConsents?.message) && (
										<span className="errorMessage">
											{formatMessage({
												id: "komid.register.business.consents.accept.error.message",
											})}
										</span>
									)}
								</>
							)}

							<div className="form-text inner-space-8">
								<strong>
									{formatMessage({
										id: "komid.register.business.consents.optional",
									})}
								</strong>
								<div>
									<Checkbox
										id="acceptSmsNotifications"
										ref={
											register("acceptSmsNotifications", {
												value: acceptSmsNotifications,
											})?.ref
										}
										error={
											typeof errors.acceptSmsNotifications?.message === "string"
												? errors.acceptSmsNotifications.message
												: undefined
										}
										value={acceptSmsNotifications}
										className="form-input"
										label={formatMessage({
											id: "consents.clubSms.text",
										})}
										onChange={async ({ target: { id, value } }) => {
											value
												? clearErrors("acceptSmsNotifications")
												: await trigger("acceptSmsNotifications");
											setValue("acceptSmsNotifications", normalizeValue(value));

											dispatch(
												handleFieldValueChange({
													form: target,
													field: id,
													value: normalizeValue(value),
												}),
											);
										}}
									/>
								</div>
							</div>

							{!enableClubAgreementConsent && (
								<div className="form-text">
									<a
										className="form-link"
										onClick={() =>
											dispatch(
												showGeneralModal({
													formattedTitle: {
														id: "komid.register.business.consents.modal.title",
													},
													content: formatMessage({
														id: "consents.club.text",
													}),
													isHtml: true,
												}),
											)
										}
										data-testid="registration.b2b.form.consents.readmore.link"
										href="#"
									>
										{formatMessage({
											id: "komid.register.business.consents.open.and.read",
										})}
									</a>
								</div>
							)}
						</div>
					</div>
				</>
			) : (
				<>
					<Checkbox
						key="acceptOffersInformations"
						id="acceptOffersInformations"
						ref={
							register("acceptOffersInformations", {
								value: acceptOffersInformations,
							})?.ref
						}
						error={
							typeof errors.acceptOffersInformations?.message === "string"
								? errors.acceptOffersInformations.message
								: undefined
						}
						value={acceptOffersInformations}
						className="form-input"
						label={formatMessage({
							id: "Consents.Newsletter.Text",
						})}
						linkText={formatMessage({
							id: "komid.register.wizard.offers.information.textLink",
						})}
						onChange={async ({ target: { id, value } }) => {
							value
								? clearErrors("acceptOffersInformations")
								: await trigger("acceptOffersInformations");
							setValue("acceptOffersInformations", normalizeValue(value));

							dispatch(
								handleFieldValueChange({
									form: target,
									field: id,
									value: normalizeValue(value),
								}),
							);
						}}
						onLinkClick={() =>
							dispatch(
								showGeneralModal({
									formattedTitle: {
										id: "gdpr.consents.information.modal.title",
									},
									formattedContent: {
										id: "gdpr.consents.information.modal.info",
										tagName: "div",
									},
								}),
							)
						}
					/>
				</>
			)}
		</>
	);
};
